import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useTheme } from 'contexts/ThemeContext';
import { AlertTriangle, X, RotateCcw } from 'lucide-react';

interface InstagramReloginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRelogin: () => void;
  accountName?: string;
  lockTitle?: { en: string; fr: string };
  lockDescription?: { en: string; fr: string };
  lockedByAdmin?: boolean;
}

const InstagramReloginModal: React.FC<InstagramReloginModalProps> = ({
  isOpen,
  onClose,
  onRelogin,
  accountName = "your Instagram account",
  lockTitle,
  lockDescription,
  lockedByAdmin = false,
}) => {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();

  if (!isOpen) return null;

  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const hasAdminLockInfo = !!(lockTitle?.en && lockTitle?.fr && lockDescription?.en && lockDescription?.fr);

  // Color tokens
  const overlayBg = isDark ? 'rgba(2, 6, 24, 0.75)' : 'rgba(241, 245, 249, 0.75)';
  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const modalBorder = isDark ? '#1D293D' : '#F1F5F9';
  const modalShadow = isDark
    ? '0px 10px 15px -3px rgba(2, 6, 24, 0.5), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)'
    : '0px 10px 15px -3px rgba(43, 127, 255, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const closeColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const cardTitleColor = isDark ? '#CAD5E2' : '#314158';
  const cardTextColor = isDark ? '#90A1B9' : '#62748E';
  const warningBg = isDark ? 'rgba(255, 186, 0, 0.08)' : '#FFFBEB';
  const warningBorder = isDark ? '#461901' : '#FDE68A';
  const warningIconColor = isDark ? '#FFBA00' : '#D97706';
  const warningTextColor = isDark ? '#CAD5E2' : '#62748E';
  const laterBg = isDark ? '#1D293D' : '#F8FAFC';
  const laterBorder = isDark ? '#314158' : '#E2E8F0';
  const laterColor = isDark ? '#E2E8F0' : '#314158';
  const laterHoverBg = isDark ? '#314158' : '#F1F5F9';

  const modalContent = (
    <div
      style={{
        position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: overlayBg, backdropFilter: 'blur(8px)', zIndex: 10000, padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: '420px',
          background: modalBg, border: `1px solid ${modalBorder}`,
          borderRadius: '16px', boxShadow: modalShadow, overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 20px', borderBottom: `1px solid ${headerBorder}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '36px', height: '36px', borderRadius: '12px',
                background: isDark ? '#461901' : '#FEF3C7',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <AlertTriangle size={18} color={warningIconColor} />
            </div>
            <span style={{ fontSize: '16px', fontWeight: 600, color: titleColor }}>
              {hasAdminLockInfo
                ? (lang === 'fr' ? lockTitle!.fr : lockTitle!.en)
                : t('modals.relogin.title')}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}
          >
            <X size={18} color={closeColor} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Account Info Card */}
          <div
            style={{
              background: cardBg, border: `1px solid ${cardBorder}`,
              borderRadius: '12px', padding: '14px',
            }}
          >
            <p style={{ fontSize: '14px', fontWeight: 500, color: cardTitleColor, margin: 0, marginBottom: '4px' }}>
              {t('modals.relogin.accountRequiresRelogin', { accountName })}
            </p>
            <p style={{ fontSize: '13px', fontWeight: 400, color: cardTextColor, margin: 0 }}>
              {hasAdminLockInfo
                ? (lang === 'fr' ? lockDescription!.fr : lockDescription!.en)
                : t('modals.relogin.suspendedMessage')}
            </p>
          </div>

          {/* Warning Note - only show for non-admin locks */}
          {!lockedByAdmin && (
            <div
              style={{
                background: warningBg, border: `1px solid ${warningBorder}`,
                borderRadius: '12px', padding: '14px',
                display: 'flex', gap: '10px', alignItems: 'flex-start',
              }}
            >
              <AlertTriangle size={14} color={warningIconColor} style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ fontSize: '13px', fontWeight: 400, color: warningTextColor, margin: 0, lineHeight: '20px' }}>
                {t('modals.relogin.warningNote')}
              </p>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <button
              onClick={onClose}
              onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.background = laterHoverBg; }}
              onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.background = laterBg; }}
              style={{
                flex: 1, padding: '10px 16px', fontSize: '14px', fontWeight: 500,
                background: laterBg, border: `1px solid ${laterBorder}`, color: laterColor,
                borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s',
              }}
            >
              {t('modals.relogin.later')}
            </button>
            <button
              onClick={onRelogin}
              style={{
                flex: 1, padding: '8px 12px', fontSize: '14px', fontWeight: 500,
                background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                border: 'none', color: '#FFFFFF', borderRadius: '8px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => { (e.target as HTMLButtonElement).style.opacity = '0.9'; }}
              onMouseLeave={(e) => { (e.target as HTMLButtonElement).style.opacity = '1'; }}
            >
              <RotateCcw size={14} />
              {t('modals.relogin.reloginNow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default InstagramReloginModal;
